import Link from "next/link";

import { HowToJsonLd, NextSeo } from "next-seo";

import ShimmerButton from "@/components/magicui/shimmer-button";
import Footer from "@/components/marketing/pieces/Footer";
import { getAllPages, getPageBySlug } from "@/lib/contentful";

interface FormattedTutorialStep {
  stepTitle: string;
  stepDescription: string;
}

export interface Tutorial {
  title: string;
  seoTitle: string;
  seoDescription: string;
  slug: string;
  tag: string;
  embedId?: string;
  steps: string[];
  createdAt: string;
  updatedAt: string;
}

export default function TutorialPage({ tutorial }: { tutorial: Tutorial }) {
  const formattedTutorialSteps = tutorial.steps.map((step) => {
    const [stepTitle, ...descriptionParts] = step.split(".");
    return {
      stepTitle: stepTitle.trim(),
      stepDescription: descriptionParts.join(".").trim(),
    };
  });

  return (
    <>
      <NextSeo
        title={tutorial.seoTitle}
        description={tutorial.seoDescription}
        canonical={`https://rebolthq.com/tutorials/${tutorial.slug}`}
        openGraph={{
          images: [
            {
              url: `https://api.screenshotone.com/take?access_key=cQWOH7FyQTatDw&url=https://rebolthq.com/tutorials/${tutorial.slug}&full_page=false&viewport_width=1200&viewport_height=850&device_scale_factor=1&format=jpg&image_quality=80&cache=true&cache_ttl=304800`,
            },
          ],
        }}
      />
      <HowToJsonLd
        name={tutorial.title}
        step={formattedTutorialSteps.map((step, index) => ({
          url: `https://rebolthq.com/tutorials/${tutorial.slug}#step${index + 1}`,
          name: step.stepTitle,
          text: step.stepDescription,
        }))}
      />

      <header className="w-full bg-white shadow-sm sticky top-0 z-10 py-3">
        <div className="max-w-6xl mx-auto flex justify-between items-center px-4 md:px-0">
          <img src="/rebolt-branding/rebolt-full-dark.png" className="h-8 relative" />
          <div className="flex items-center gap-4">
            <p className="font-medium hidden sm:block">Do you run a service business?</p>
            <Link href="/">
              <ShimmerButton className="font-medium">Get a better website</ShimmerButton>
            </Link>
          </div>
        </div>
      </header>
      <article className="max-w-5xl mx-auto mt-16 mb-40 px-6 md:px-0">
        <div className="text-center">
          <span className="text-ash-600 text-sm font-medium px-2 py-1 rounded-md bg-rebolt-100 mb-4 inline-flex gap-3">
            <Link href="/" className="hover:underline">
              Home
            </Link>
            <span className="opacity-30">/</span>
            <Link href={`/tutorials`} className="hover:underline">
              Tutorials
            </Link>
            <span className="opacity-30">/</span>
            <Link href={`/tutorials?tag=${encodeURIComponent(tutorial.tag)}`} className="hover:underline">
              {tutorial.tag}
            </Link>
          </span>
        </div>
        <h1 className="text-4xl md:text-6xl max-w-3xl mx-auto text-center font-bold mb-16">{tutorial.title}</h1>
        <div
          style={{
            position: "relative",
            boxSizing: "content-box",
            maxHeight: "80vh",
            width: "100%",
            aspectRatio: 2.1867881548974943,
            padding: "40px 0",
          }}
        >
          {tutorial.embedId && (
            <iframe
              src={`https://tutorials.rebolthq.com/embed/${tutorial.embedId}?embed_v=2`}
              loading="lazy"
              title={tutorial.title}
              allow="clipboard-write"
              allowFullScreen
              className="border-none"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          )}
        </div>

        <div className="max-w-3xl mx-auto mt-20">
          <h3 className="text-2xl font-semibold mb-6">Steps:</h3>
          <ol className="">
            {formattedTutorialSteps.map((step, index) => (
              <li key={index} className="list-inside list-decimal mb-6" id={`step${index + 1}`}>
                <h2 className="font-semibold inline-block">{step.stepTitle}</h2>
                <p className="text-ash-700">{step.stepDescription}</p>
              </li>
            ))}
          </ol>
        </div>
        <CTA />
      </article>
      <Footer />
    </>
  );
}

export const getStaticProps = async ({ params }: { params: { tutorialSlug: string } }) => {
  const tutorial = await getPageBySlug(params.tutorialSlug, "pageTutorial");

  if (!tutorial) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      tutorial,
    },
    revalidate: 3600 * 24 * 7, // Revalidate every 7 days
  };
};

export const getStaticPaths = async () => {
  const tutorials = await getAllPages(1, 100, "pageTutorial");

  const paths = tutorials.pages.map((tutorial: Pick<Tutorial, "slug">) => ({
    params: { tutorialSlug: tutorial.slug },
  }));

  return {
    paths,
    fallback: "blocking",
  };
};

function CTA() {
  return (
    <div className="mt-8 md:mt-20 ">
      <div className="mx-auto max-w-7xl py-16 lg:px-8 xl:-mx-20 ">
        <div className="rounded-lg bg-rebolt-700 shadow-xl lg:grid lg:grid-cols-2 lg:gap-4 relative">
          <div className="px-6 pb-12 pt-10 sm:px-16 sm:pt-16 lg:py-16 lg:pr-0 xl:px-20 xl:py-20">
            <div className="lg:self-center">
              <h3 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                <span className="block">
                  We help <span className="text-ash-900">home service businesses</span> get found online
                </span>
              </h3>
              <p className="mt-4 text-lg leading-6 text-rebolt-200">
                Let us set you up with a free site and handle your social media management.
              </p>
              <Link
                href="/sign-up"
                className="mt-8 inline-flex items-center rounded-md border border-transparent bg-white px-5 py-3 text-base font-medium text-rebolt-600 shadow-sm hover:bg-rebolt-100"
              >
                Get Started Now
              </Link>
            </div>
          </div>

          <img
            alt="Logo"
            src="/rebolt-branding/rebolt-full-white.png"
            className="h-16 hidden lg:block absolute top-10 right-10"
          />
        </div>
      </div>
    </div>
  );
}
